import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  Row,
  Col,
  Table,
  Input,
  Select,
  Button,
  Tooltip,
  Tag,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getGroup } from "redux/actions/group.action";
import { getFacility } from "redux/actions/facility.action";
import { getRoleList } from "redux/actions/getRole.action";
import { getApiArrayDetails } from "utils/MyMUtils";
import { getApi } from "utils/apiCall";
import { INITIAL_COMPLIANCE } from "./staffModalShared";

const { Option } = Select;

const DEFAULT_PAGE_SIZE = 10;
const FALLBACK_TEXT = "-";
const SEARCH_DEBOUNCE_MS = 300;
const STATUS_FILTER_OPTIONS = [
  { value: "compliant", label: "Compliant" },
  { value: "non-compliant", label: "Non-Compliant" },
  { value: "attention", label: "Attention" },
];

const getFullNameParts = (item = {}) => {
  const firstName = item.first_name || item.firstName || "";
  const lastName = item.last_name || item.lastName || "";

  if (firstName || lastName) {
    return { firstName, lastName };
  }

  const fullName = (item.full_name || item.name || "").trim();
  if (!fullName) {
    return { firstName: "", lastName: "" };
  }

  const [first, ...rest] = fullName.split(/\s+/);
  return { firstName: first || "", lastName: rest.join(" ") };
};

const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

const getStatusConfig = (status, color) => {
  const normalizedStatus = normalizeStatusKey(status);
  const fallbackMap = {
    compliant: { color: "#1A8F5C", text: "Compliant" },
    attention: { color: "#B4690E", text: "Attention" },
    "non-compliant": { color: "#C0392B", text: "Non-compliant" },
    unknown: { color: "#5B6472", text: "Unknown" },
  };

  return {
    ...(fallbackMap[normalizedStatus] || fallbackMap.unknown),
    ...(color ? { color } : {}),
    text: status || fallbackMap[normalizedStatus]?.text || fallbackMap.unknown.text,
  };
};

const getCollection = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
};

const getListingPayload = (response) => {
  const payload = response?.result ?? response ?? {};
  // New API shape: { data: [], total_count: N }
  if (Array.isArray(payload?.data)) {
    return { items: payload.data, total: payload.total_count ?? payload.data.length };
  }
  // Fallback for older shapes
  const collection = getCollection(
    payload?.rows,
    payload?.staff,
    payload?.staffs,
    payload?.listing,
    payload?.items,
    payload?.result,
    Array.isArray(payload) ? payload : null
  );
  return {
    items: collection,
    total: payload?.total ?? payload?.total_count ?? payload?.count ?? collection.length,
  };
};

const getDisplayList = (items, fallback = FALLBACK_TEXT) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { names: [], text: fallback };
  }

  const names = items
    .map((item) => {
      if (typeof item === "string") {
        return item.trim();
      }

      return (
        item?.facility_name ||
        item?.name ||
        item?.facility ||
        item?.role_name ||
        item?.role ||
        item?.label ||
        ""
      ).trim();
    })
    .filter(Boolean);

  return {
    names,
    text: names.length ? names.join(", ") : fallback,
  };
};

const getFacilitiesSelection = (facilities = []) =>
  facilities.reduce((acc, facility) => {
    const facilityId = facility?.facility_id ?? facility?.id;
    if (facilityId === undefined || facilityId === null) {
      return acc;
    }

    acc[facilityId] = true;
    return acc;
  }, {});

const getFacilityModulesSelection = (facilities = []) =>
  facilities.reduce((acc, facility) => {
    const facilityId = facility?.facility_id ?? facility?.id;
    if (facilityId === undefined || facilityId === null) {
      return acc;
    }

    acc[facilityId] = (facility?.modules || []).reduce((moduleAcc, moduleItem) => {
      const moduleId = moduleItem?.module_id ?? moduleItem?.id;
      if (moduleId !== undefined && moduleId !== null) {
        moduleAcc[moduleId] = true;
      }
      return moduleAcc;
    }, {});

    return acc;
  }, {});

const mapStaffRecord = (item, rolesMap = {}) => {
  const facilityList = getCollection(item?.facilities);
  const roleList = getCollection(item?.roles);
  const { names: facilityNames, text: facilityText } = getDisplayList(facilityList);

  // Resolve role name: prefer roles array, then rolesMap by role_id, then item.role string
  const roleId = item?.role_id ?? item?.roleId ?? roleList[0]?.role_id ?? null;
  const resolvedRoleName =
    (roleList.length ? getDisplayList(roleList).text : null) ||
    (roleId != null ? rolesMap[roleId] : null) ||
    item?.role ||
    FALLBACK_TEXT;

  const { firstName, lastName } = getFullNameParts(item);
  const fullName =
    (item?.full_name || `${firstName} ${lastName}`.trim() || item?.name || "").trim() ||
    "Unnamed Staff";

  return {
    id: item?.id,
    name: fullName,
    organisation:
      item?.organization_name || item?.organisation_name || FALLBACK_TEXT,
    facility: facilityText,
    facilityNames,
    role: resolvedRoleName,
    roleNames: resolvedRoleName !== FALLBACK_TEXT ? [resolvedRoleName] : [],
    compliance: normalizeStatusKey(item?.overall_status) || "unknown",
    complianceLabel: item?.overall_status || "Unknown",
    complianceColor: item?.overall_status_color,
    complianceReason: item?.overall_status_reason || "",
    staffId: item?.id,
    firstName,
    lastName,
    mobileNo: item?.mobile_no || item?.mobileNo || item?.mobile || "",
    isAgencyStaff: Boolean(item?.is_agency_staff ?? item?.isAgencyStaff),
    roleId,
    facilities: getFacilitiesSelection(facilityList),
    facilityModules: getFacilityModulesSelection(facilityList),
    username: item?.username || item?.user_name || "",
    email: item?.email || "",
    compliance_data: item?.compliance_data || INITIAL_COMPLIANCE,
  };
};

const renderListCell = (values, fallback = FALLBACK_TEXT) => {
  if (!values?.length) {
    return <span>{fallback}</span>;
  }

  const content = values.join(", ");
  return (
    <Tooltip title={content}>
      <span>{content}</span>
    </Tooltip>
  );
};

const FACILITY_VISIBLE_COUNT = 2;

const FacilityChips = memo(({ values, fallback = FALLBACK_TEXT }) => {
  if (!values?.length) {
    return <span style={{ color: "#bfbfbf" }}>{fallback}</span>;
  }

  const visible = values.slice(0, FACILITY_VISIBLE_COUNT);
  const overflow = values.slice(FACILITY_VISIBLE_COUNT);

  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      {visible.map((name) => (
        <Tag
          key={name}
          style={{
            margin: 0,
            borderRadius: 5,
            fontSize: 12,
            maxWidth: 160,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Tag>
      ))}
      {overflow.length > 0 && (
        <Tooltip
          title={
            <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {overflow.map((name) => (
                <span key={name}>{name}</span>
              ))}
            </span>
          }
        >
          <Tag
            style={{
              margin: 0,
              borderRadius: 4,
              fontSize: 12,
              cursor: "pointer",
              borderColor: "#2f6fe0",
              color: "#2f6fe0",
              background: "#f0f5fb",
            }}
          >
            +{overflow.length} more
          </Tag>
        </Tooltip>
      )}
    </span>
  );
});

const ComplianceBadge = memo(({ status, color, reason }) => {
  const statusConfig = getStatusConfig(status, color);
  const badge = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: statusConfig.color,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      <span style={{ color: "#101828" }}>{statusConfig.text}</span>
      {/* {reason ? (
        <Tag
          color="default"
          style={{ marginInlineEnd: 0, borderRadius: 999, color: "#5B6472" }}
        >
          {reason}
        </Tag>
      ) : null} */}
    </span>
  );

  if (!reason) {
    return badge;
  }

  return <Tooltip title={reason}>{badge}</Tooltip>;
});

const ActionButtons = memo(({ record, onView, onEdit, onDelete }) => (
  <span style={{ display: "inline-flex", gap: 8 }}>
    <Tooltip title="View">
      <Button
        size="small"
        style={{
          minWidth: 32,
          fontWeight: 600,
          fontSize: 12,
          borderColor: "#2f6fe0",
          color: "#2f6fe0",
        }}
        onClick={() => onView(record)}
        icon={<EyeOutlined />}
      />
    </Tooltip>
    <Tooltip title="Edit">
      <Button
        size="small"
        type="primary"
        style={{
          minWidth: 32,
          fontWeight: 600,
          fontSize: 12,
          background: "#2f6fe0",
        }}
        onClick={() => onEdit(record)}
        icon={<EditOutlined />}
      />
    </Tooltip>
    <Tooltip title="Delete">
      <Button
        size="small"
        danger
        style={{
          minWidth: 32,
          fontWeight: 600,
          fontSize: 12,
        }}
        onClick={() => onDelete(record)}
        icon={<DeleteOutlined />}
      />
    </Tooltip>
  </span>
));

const StaffDirectory = ({ onEdit, onView, onDelete, refreshKey, onDataChange, onFiltersChange }) => {
  const dispatch = useDispatch();
  const roles        = useSelector((state) => getApiArrayDetails(state.getRole.data, []));
  const groupList    = useSelector((state) => getApiArrayDetails(state.GroupList.data, []));
  const facilitiesList = useSelector((state) => getApiArrayDetails(state.FacilitiesList.data, []));
  const [searchText, setSearchText]             = useState("");
  const [selectedGroup, setSelectedGroup]       = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedRole, setSelectedRole]         = useState(null);
  const [selectedStatus, setSelectedStatus]     = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  const listingQuery = useMemo(() => {
    const params = new URLSearchParams({
      page: String(currentPage),
      limit: String(currentPageSize),
    });
    const trimmedName = debouncedSearchText.trim();
    if (trimmedName)      params.set("name", trimmedName);
    if (selectedRole)     params.set("role", selectedRole);
    if (selectedFacility) params.set("facility_id", String(selectedFacility));
    if (selectedGroup)    params.set("org_id", String(selectedGroup));
    if (selectedStatus)   params.set("status", selectedStatus);
    return params.toString();
  }, [currentPage, currentPageSize, debouncedSearchText, selectedRole, selectedFacility, selectedGroup, selectedStatus]);

  useEffect(() => {
    dispatch(getRoleList());
    dispatch(getGroup());
  }, [dispatch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  // When group changes, reset facility and fetch new facility list
  useEffect(() => {
    setSelectedFacility(null);
    if (selectedGroup) dispatch(getFacility(selectedGroup));
  }, [dispatch, selectedGroup]);

  useEffect(() => {
    setPagination((prev) =>
      prev.current === 1 ? prev : { ...prev, current: 1 }
    );
    if (typeof onFiltersChange === "function") {
      onFiltersChange({
        name: debouncedSearchText.trim() || undefined,
        role: selectedRole || undefined,
        facilityId: selectedFacility || undefined,
        groupId: selectedGroup || undefined,
        status: selectedStatus || undefined,
      });
    }
  }, [debouncedSearchText, selectedRole, selectedFacility, selectedGroup, selectedStatus]);

  useEffect(() => {
    let isMounted = true;

    const fetchStaff = async () => {
      setTableLoading(true);

      const { error, result } = await getApi(`/api/staff/listing?${listingQuery}`);

      if (!isMounted) {
        return;
      }

      if (error) {
        setStaffList([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        toast.error("Unable to load staff directory.");
        setTableLoading(false);
        return;
      }

      const { items, total } = getListingPayload(result);
      const rolesMap = Object.fromEntries(
        (roles || []).map((r) => [r.role_id, r.role_name])
      );
      setStaffList(Array.isArray(items) ? items.map((item) => mapStaffRecord(item, rolesMap)) : []);
      if (typeof onDataChange === "function") {
        onDataChange(Array.isArray(items) ? items.map((item) => mapStaffRecord(item, rolesMap)) : []);
      }
      setPagination((prev) => ({
        ...prev,
        total: Number(total) || 0,
      }));
      setTableLoading(false);
    };

    fetchStaff();

    return () => {
      isMounted = false;
    };
  }, [listingQuery, refreshKey]);

  const roleFilterOptions = useMemo(
    () =>
      (roles || [])
        .map((role) => ({ value: role?.role_name || "", label: role?.role_name || "" }))
        .filter((r) => r.value),
    [roles]
  );

  const facilityFilterOptions = useMemo(
    () =>
      (facilitiesList || [])
        .map((f) => ({ value: f?.facility_id, label: f?.facility_name || "" }))
        .filter((f) => f.value),
    [facilitiesList]
  );

  const groupFilterOptions = useMemo(
    () =>
      (groupList || [])
        .map((g) => ({ value: g?.org_id, label: g?.org_name || "" }))
        .filter((g) => g.value),
    [groupList]
  );

  const handleTableChange = useCallback((nextPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current || prev.current,
      pageSize: nextPagination.pageSize || prev.pageSize,
    }));

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleEdit = useCallback(
    (record) =>
      onEdit({
        staffId: record.staffId,
        firstName: record.firstName,
        lastName: record.lastName,
        mobileNo: record.mobileNo,
        isAgencyStaff: record.isAgencyStaff,
        roleId: record.roleId,
        facilities: record.facilities,
        facilityModules: record.facilityModules,
        username: record.username,
        email: record.email,
        compliance: record.compliance_data,
      }),
    [onEdit]
  );

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: "Group",
        dataIndex: "organisation",
        key: "organisation",
      },
      {
        title: "Role",
        dataIndex: "roleNames",
        key: "role",
        render: (values) => renderListCell(values),
      },
      {
        title: "Facility",
        dataIndex: "facilityNames",
        key: "facility",
        render: (values) => <FacilityChips values={values} />,
      },
      {
        title: "Compliance",
        dataIndex: "complianceLabel",
        key: "compliance",
        render: (_, record) => (
          <ComplianceBadge
            status={record.complianceLabel}
            color={record.complianceColor}
            reason={record.complianceReason}
          />
        ),
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => (
          <ActionButtons 
            record={record} 
            onView={() => onView(record)} 
            onEdit={handleEdit}
            onDelete={() => onDelete(record)}
          />
        ),
      },
    ],
    [handleEdit]
  );

  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col flex="1 1 200px">
          <Input
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Search staff"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="1 1 160px">
          <Select
            placeholder="Group"
            style={{ width: "100%" }}
            allowClear={!!selectedGroup}
            value={selectedGroup}
            onChange={(val) => setSelectedGroup(val || null)}
          >
            {groupFilterOptions.map((g) => (
              <Option key={g.value} value={g.value}>{g.label}</Option>
            ))}
          </Select>
        </Col>
        <Col flex="1 1 160px">
          <Select
            placeholder="Facility"
            style={{ width: "100%" }}
            allowClear={!!selectedFacility}
            disabled={!selectedGroup}
            value={selectedFacility}
            onChange={setSelectedFacility}
          >
            {facilityFilterOptions.map((f) => (
              <Option key={f.value} value={f.value}>{f.label}</Option>
            ))}
          </Select>
        </Col>
        <Col flex="1 1 180px">
          <Select
            placeholder="Role"
            style={{ width: "100%" }}
            allowClear={!!selectedRole}
            value={selectedRole}
            onChange={setSelectedRole}
          >
            {roleFilterOptions.map((role) => (
              <Option key={role.value} value={role.value}>{role.label}</Option>
            ))}
          </Select>
        </Col>
        <Col flex="0 0 120px">
          <Select
            placeholder="Status"
            style={{ width: "100%" }}
            allowClear={!!selectedStatus}
            value={selectedStatus}
            onChange={setSelectedStatus}
          >
            {STATUS_FILTER_OPTIONS.map((status) => (
              <Option key={status.value} value={status.value}>
                {status.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={staffList}
        rowKey="id"
        size="middle"
        loading={tableLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
        locale={{ emptyText: "No staff records found." }}
        style={{ background: "#fff" }}
      />
    </div>
  );
};

export default memo(StaffDirectory);
